// Web Push helpers — register service worker, subscribe / unsubscribe,
// keep the backend in sync. Everything here is best-effort: a phone with
// notifications denied or a desktop browser without push support should
// fall through silently so the rest of the app still works.

import { api } from "../api";

const SW_URL = "/sw.js";

export type PushState =
  | { kind: "unsupported" }
  | { kind: "denied" }
  | { kind: "disabled" } // server has no VAPID keys
  | { kind: "ready"; subscribed: boolean };

export function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// registerServiceWorker idempotently registers /sw.js. Safe to call on
// every app load — the browser dedupes.
//
// Ausdrücklich *nicht* an pushSupported gebunden.
//
// Der Arbeiter tut zwei Dinge: er nimmt Push-Nachrichten entgegen, und er
// beantwortet einen Seitenaufruf, der ins Leere läuft, mit unserer eigenen
// Offline-Seite statt mit der weißen von Cloudflare. Das zweite hat mit
// Push nichts zu tun — und hing trotzdem daran: fehlt `PushManager` oder
// `Notification` (ein Browser im privaten Fenster, Benachrichtigungen im
// System abgeschaltet), wurde gar kein Arbeiter angemeldet, und damit gab
// es auch keine Offline-Seite. Genau der Fall, der gemeldet wurde.
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
    return reg;
  } catch (err) {
    console.warn("[push] SW registration failed", err);
    return null;
  }
}

export async function getState(): Promise<PushState> {
  if (!pushSupported()) return { kind: "unsupported" };
  if (Notification.permission === "denied") return { kind: "denied" };
  try {
    await api<{ key: string }>("/push/vapid-key");
  } catch {
    return { kind: "disabled" };
  }
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  return { kind: "ready", subscribed: existing != null };
}

// subscribe asks for permission, creates a PushSubscription, and stores
// it on the backend. Returns true on full success.
export async function subscribe(): Promise<boolean> {
  if (!pushSupported()) return false;

  // Notification.requestPermission must be called from a user gesture on
  // most browsers — caller has to invoke this from a button onClick.
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return false;

  const reg = await registerServiceWorker();
  if (!reg) return false;
  await navigator.serviceWorker.ready;

  const { key } = await api<{ key: string }>("/push/vapid-key");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    });
  }

  await api("/push/subscribe", {
    method: "POST",
    body: JSON.stringify(sub.toJSON()),
  });
  return true;
}

export async function unsubscribe(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  // Order: tell the server first so we don't end up with a live FCM
  // endpoint that the server has lost. If the unsubscribe call fails
  // the local sub stays — re-running heals it.
  try {
    await api("/push/unsubscribe", {
      method: "POST",
      body: JSON.stringify({ endpoint }),
    });
  } catch (err) {
    console.warn("[push] backend unsubscribe failed", err);
  }
  await sub.unsubscribe();
}

export async function sendTest(): Promise<number> {
  const r = await api<{ delivered: number }>("/push/test", { method: "POST" });
  return r.delivered;
}

// VAPID keys are url-safe base64. PushManager wants a Uint8Array — this
// is the canonical conversion lifted straight from the Web Push spec.
function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
